import { AuthGuard } from "@elit-hair/elit-tools";
import { Button, EmptyState, ErrorState, Icon, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbCalendarTime, TbRefresh } from "react-icons/tb";
import z from "zod";

import { PageActions, PageToolbarTime } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { PERMISSIONS } from "@/constants/permissions";
import { getBrowserTimezone } from "@/features/schedule/utils/schedule-timezone.utils";
import {
    projectBookingCountsFromStorage,
    projectSlotToStorage,
    projectSlotsFromStorage,
} from "@/features/schedule/utils/slot-timezone.utils";
import { TimezoneSelect } from "@/pages/management/schedules/components/schedule-edit-views";
import { BOOKING_STATUS, useBookings } from "@/queries/booking.queries";
import { useEventType } from "@/queries/event-type.queries";
import type { BookableSlot } from "@/queries/slot.queries";
import { useEventTypeSlots, useRegenerateEventTypeSlots } from "@/queries/slot.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { BookAppointmentDialog } from "./components/book-appointment-dialog";
import { BookableSlotsGrid } from "./components/bookable-slots-grid";
import { BookableSlotsWeekSelect } from "./components/bookable-slots-week-select";
import { useBookableSlotsDateRange } from "./hooks/use-bookable-slots-date-range";
import { BOOKABLE_SLOTS_WINDOW_DAYS } from "./utils/bookable-slots-window";

const SlotsSearchSchema = z.object({
    startDate: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.EVENT_SLOTS)({
    component: BookableSlotsPage,
    validateSearch: searchValidator(SlotsSearchSchema),
});

type SelectedBookingSlot = {
    date: string;
    slot: BookableSlot;
};

function BookableSlotsPage() {
    const { t, i18n } = useTranslation();
    const { id: eventTypeId } = Route.useParams();
    const search = Route.useSearch({ structuralSharing: true });
    const navigate = Route.useNavigate();
    const regenerateSlotsMutation = useRegenerateEventTypeSlots();
    const [displayTimezone, setDisplayTimezone] = useState(getBrowserTimezone);
    const [selectedBooking, setSelectedBooking] = useState<SelectedBookingSlot | null>(null);

    const { data: eventType, isPending: isEventTypePending } = useEventType(eventTypeId);

    const updateSearch = useCallback(
        (updates: { startDate?: string }) => {
            navigate({
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    const {
        startDate,
        startDateStr,
        endDateStr,
        datesInRange,
        minDate,
        maxDate,
        horizonEnd,
        continuesAfterWeek,
        handleStartDateChange,
    } = useBookableSlotsDateRange({
        search,
        onSearchChange: updateSearch,
        maxBookingDaysAhead: eventType?.maxBookingDaysAhead ?? BOOKABLE_SLOTS_WINDOW_DAYS,
    });

    const {
        data: slots,
        isPending: isSlotsPending,
        isError: isSlotsError,
        error: slotsError,
        refetch: refetchSlots,
    } = useEventTypeSlots(eventTypeId, startDateStr, endDateStr);

    const {
        data: bookings,
        isPending: isBookingsPending,
        isError: isBookingsError,
        error: bookingsError,
        refetch: refetchBookings,
    } = useBookings({
        eventTypeId,
        dateFrom: startDateStr,
        dateTo: endDateStr,
        status: BOOKING_STATUS.CONFIRMED,
    });

    const displaySlots = useMemo(() => projectSlotsFromStorage(slots ?? {}, displayTimezone), [slots, displayTimezone]);

    const bookingCounts = useMemo(() => {
        const counts: Record<string, Record<string, number>> = {};

        for (const booking of bookings ?? []) {
            if (!counts[booking.date]) counts[booking.date] = {};
            counts[booking.date][booking.startTime] = (counts[booking.date][booking.startTime] ?? 0) + 1;
        }

        return projectBookingCountsFromStorage(counts, displayTimezone);
    }, [bookings, displayTimezone]);

    const totalBookableSlots = useMemo(
        () =>
            datesInRange.reduce(
                (sum, date) => sum + (displaySlots[date] ?? []).filter((slot) => slot.count > 0).length,
                0
            ),
        [datesInRange, displaySlots]
    );

    const totalBookings = useMemo(() => {
        let count = 0;

        for (const date of datesInRange) {
            const dayCounts = bookingCounts[date];
            if (!dayCounts) continue;
            for (const slotCount of Object.values(dayCounts)) {
                count += slotCount;
            }
        }

        return count;
    }, [datesInRange, bookingCounts]);

    const laterSlotsUntil = horizonEnd.setLocale(i18n.language).toLocaleString(DateTime.DATE_MED);

    const isLoading = isEventTypePending || isSlotsPending || isBookingsPending;
    const isError = isSlotsError || isBookingsError;
    const error = slotsError ?? bookingsError;

    const refetch = useCallback(() => {
        refetchSlots();
        refetchBookings();
    }, [refetchSlots, refetchBookings]);

    const handleRegenerateSlots = useCallback(() => {
        regenerateSlotsMutation.mutate(eventTypeId);
    }, [regenerateSlotsMutation, eventTypeId]);

    const handleSlotClick = useCallback(
        (date: string, slot: BookableSlot) => {
            setSelectedBooking(projectSlotToStorage(date, slot, displayTimezone));
        },
        [displayTimezone]
    );

    const handleBookingDialogOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setSelectedBooking(null);
        }
    }, []);

    return (
        <div className="flex h-0 min-h-0 flex-1 flex-col gap-3">
            <PageToolbarTime>
                <TimezoneSelect value={displayTimezone} onChange={setDisplayTimezone} className="w-48" />
                <BookableSlotsWeekSelect
                    startDate={startDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onWeekChange={handleStartDateChange}
                />
            </PageToolbarTime>

            <PageActions>
                <AuthGuard required={[PERMISSIONS.CALL_CALENDAR_SLOT_REGENERATE]}>
                    <Button
                        variant="outline"
                        onClick={handleRegenerateSlots}
                        disabled={regenerateSlotsMutation.isPending}
                    >
                        <Icon icon={TbRefresh} />
                        {regenerateSlotsMutation.isPending ? t("loading") : t("regenerate_slots")}
                    </Button>
                </AuthGuard>
            </PageActions>

            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm border border-emerald-500/40 bg-emerald-500/20" />
                    <span>{t("available")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm border border-sky-500/40 bg-sky-500/20" />
                    <span>{t("elitcal_fully_booked")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm border border-dashed border-border/60 bg-muted/30" />
                    <span>{t("available")}: 0</span>
                </div>
                {!isLoading && (
                    <span className="text-muted-foreground/80">
                        {t("bookable_slots_count", { count: totalBookableSlots })} ·{" "}
                        {t("event_bookings_count", { count: totalBookings })}
                    </span>
                )}
                {eventType?.isActive === false && (
                    <span className="rounded-md border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-destructive">
                        {t("inactive")}
                    </span>
                )}
            </div>

            {eventType && continuesAfterWeek && (
                <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                    <Icon icon={TbAlertTriangle} className="mt-0.5 shrink-0" />
                    <span>
                        {t("bookable_slots_continue_after_week", {
                            date: laterSlotsUntil,
                            days: eventType.maxBookingDaysAhead,
                        })}
                    </span>
                </p>
            )}

            {isError && (
                <ErrorState
                    title={t("failed_to_load_bookable_slots")}
                    message={error?.message || t("event_load_error")}
                    onRetry={refetch}
                />
            )}

            {isLoading && (
                <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-border/60 bg-muted/10">
                    <div className="flex w-full min-w-max gap-2 p-2">
                        {Array.from({ length: datesInRange.length || 7 }).map((_, index) => (
                            <Skeleton key={index} className="h-70 min-w-28 flex-1 rounded-2xl" />
                        ))}
                    </div>
                </div>
            )}

            {!isLoading && !isError && datesInRange.length === 0 && (
                <AdminSurface className="p-8">
                    <EmptyState
                        variant="simple"
                        icon={<TbCalendarTime size={40} className="text-muted-foreground" />}
                        title="no_bookable_slots_found"
                    />
                </AdminSurface>
            )}

            {!isLoading && !isError && datesInRange.length > 0 && (
                <div className="flex h-0 min-h-0 flex-1 flex-col">
                    <BookableSlotsGrid
                        datesInRange={datesInRange}
                        slots={displaySlots}
                        bookingCounts={bookingCounts}
                        onSlotClick={eventType?.isActive === false ? undefined : handleSlotClick}
                    />
                </div>
            )}

            <BookAppointmentDialog
                open={!!selectedBooking}
                onOpenChange={handleBookingDialogOpenChange}
                eventTypeId={eventTypeId}
                date={selectedBooking?.date ?? ""}
                slot={selectedBooking?.slot ?? null}
                onSuccess={refetch}
            />
        </div>
    );
}
